/*
===============================================================================================================
                                 Copyright 2026-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/**
 * Locale-aware formatting utilities using the native Intl API.
 */

/**
 * Format a date value according to the given locale.
 * @param {Date|string|number} date
 * @param {string} intlLocale - BCP 47 locale tag, e.g. 'en-US'
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export const formatLocaleDate = (date, intlLocale, options = { year: 'numeric', month: 'short', day: 'numeric' }) => {
  if (date == null || date === '') return '';

  const dateValue = typeof date === 'string' && !date.includes('T')
    ? new Date(`${date}T00:00:00`)
    : new Date(date);
  return new Intl.DateTimeFormat(intlLocale, options).format(dateValue);
};

/**
 * Format a time value according to the given locale (locale determines 12h vs 24h).
 * @param {Date|string|number} date
 * @param {string} intlLocale
 * @returns {string}
 */
export const formatLocaleTime = (date, intlLocale) => {
  if (date == null || date === '') return '';
  return new Intl.DateTimeFormat(intlLocale, { hour: 'numeric', minute: 'numeric' }).format(new Date(date));
};

/**
 * Format a date+time value according to the given locale.
 * @param {Date|string|number} date
 * @param {string} intlLocale
 * @returns {string}
 */
export const formatLocaleDateTime = (date, intlLocale) => {
  if (date == null || date === '') return '';
  return new Intl.DateTimeFormat(intlLocale, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric',
  }).format(new Date(date));
};

/**
 * Format a number according to the given locale (handles decimal/grouping separators).
 * @param {number} number
 * @param {string} intlLocale
 * @param {Intl.NumberFormatOptions} [options]
 * @returns {string}
 */
export const formatLocaleNumber = (number, intlLocale, options = {}) => new Intl.NumberFormat(intlLocale, options).format(number);

/**
 * Extract the currency symbol for a given currency code and locale.
 * Uses Intl.NumberFormat.formatToParts() so symbol position doesn't matter.
 * @param {string} currencyCode - ISO 4217 code, e.g. 'USD'
 * @param {string} intlLocale
 * @returns {string}
 */
export const getCurrencySymbol = (currencyCode, intlLocale) => {
  if (!currencyCode || !intlLocale) return '';
  try {
    return new Intl.NumberFormat(intlLocale, { style: 'currency', currency: currencyCode })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? currencyCode;
  } catch {
    return currencyCode;
  }
};

/**
 * Format a monetary amount according to the given locale and currency code.
 * @param {number} amount
 * @param {string} currencyCode - ISO 4217 code, e.g. 'USD', 'EUR'
 * @param {string} intlLocale
 * @returns {string}
 */
export const formatLocaleCurrency = (amount, currencyCode, intlLocale) => new Intl.NumberFormat(
  intlLocale,
  { style: 'currency', currency: currencyCode },
).format(amount);
